import { Suspense, type DragEvent, useState } from 'react';

import { ErrorBoundary } from '@sentry/react';
import { alpha, Box, Paper, Stack, Typography } from '@mui/material';

import DropHere from '@components/FileBrowser/Components/DropHere';
import {
  FileBrowserFileList,
  FileBrowserHeader,
  FileBrowserTableHeader,
} from '@components/FileBrowser/Components';
import {
  useAddFilesToUpload,
  useFileBrowserLocation,
} from '@components/FileBrowser/hooks';
import { PageHeader } from '@components/PageHeader';
import { MainContainer } from '@/pages/common/MainDashboard/StyledComponents';

interface IFileBrowserProps {
  onSelectFiles?: (files: string[]) => void;
  selectMultiple?: boolean;
  onClose: () => void;
}

const FileBrowser = ({
  onSelectFiles,
  selectMultiple,
  onClose,
}: IFileBrowserProps) => {
  const { location } = useFileBrowserLocation();
  const [canDrop, setCanDrop] = useState(false);
  const addFilesToUpload = useAddFilesToUpload();
  const isSelectionMode = Boolean(onSelectFiles);

  const handleOnDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    addFilesToUpload(e.dataTransfer.files, location);
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: isSelectionMode ? 'none' : 1600,
        mx: 'auto',
      }}
      onDrop={(e) => {
        handleOnDrop(e);
        setCanDrop(false);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setCanDrop(true);
      }}
      onDragLeave={() => setCanDrop(false)}
    >
      {!isSelectionMode && (
        <PageHeader
          eyebrow="Data management"
          title="Files"
          description="Store, organize, and select the source files used across clinical workflows."
        />
      )}
      <MainContainer>
        <Paper
          elevation={0}
          sx={(theme) => ({
            overflow: 'hidden',
            border: '1px solid',
            borderColor: canDrop ? 'primary.main' : 'divider',
            borderRadius: 3,
            transition: 'border-color 150ms ease, box-shadow 150ms ease',
            boxShadow: canDrop
              ? `0 0 0 4px ${alpha(theme.palette.primary.main, 0.16)}`
              : 'none',
          })}
        >
          <FileBrowserHeader location={location} showTitle={isSelectionMode} />
          <ErrorBoundary
            showDialog
            fallback={
              <Stack alignItems="center" spacing={0.5} sx={{ px: 3, py: 7 }}>
                <Typography variant="h3">Files are unavailable</Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                >
                  Refresh the page or try again shortly.
                </Typography>
              </Stack>
            }
          >
            <FileBrowserTableHeader />
            <Suspense
              fallback={
                <Stack alignItems="center" spacing={0.5} sx={{ px: 3, py: 7 }}>
                  <Typography variant="subtitle1">Loading files…</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Retrieving the selected location.
                  </Typography>
                </Stack>
              }
            >
              {!canDrop && (
                <FileBrowserFileList
                  onSelectFiles={onSelectFiles}
                  selectMultiple={selectMultiple}
                  onClose={onClose}
                />
              )}
              {canDrop && <DropHere />}
            </Suspense>
          </ErrorBoundary>
        </Paper>
      </MainContainer>
    </Box>
  );
};

export default FileBrowser;
